import { useState, useMemo, useCallback, useEffect } from 'react';
import { Table, Badge, Group, ActionIcon, Tooltip, Pagination, TextInput, Select, Grid, Button, Box, LoadingOverlay, SegmentedControl, Stack, Text, Modal, Checkbox, ScrollArea } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDispatch, useSelector } from 'react-redux';
import { getBookings, cancelBooking, deleteBooking, updateBooking } from '../../../redux/reducers/bookingSlice';
import { selectBookingsListPageSelector, selectBookingLoading } from '../../../redux/selectors/bookingSelector';
import { Booking, BookingType, BookingPaymentStatus } from '../../../models/booking.model';
import { IconEdit, IconTrash, IconCheck, IconSearch, IconFileDownload, IconX } from '@tabler/icons-react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { BookingsPDF } from './bookings_pdf/bookings_pdf';
import { debounce, getAthensDate, getBookingSourceColor, getFormattedDate, getPaymentStatusColor } from '../../../utils/helper.utils';
import { selectIsAdminOrOwner } from '../../../redux/selectors/authSelector';
import { User } from '../../../models/user.model';
import { selectUsersSelector } from '../../../redux/selectors/userSelector';
import { getAllUsers } from '../../../redux/reducers/userSlice';
import { DateTime } from 'luxon';
import { useMediaQuery } from '@mantine/hooks';
import colors from '../../../utils/styles';
import AddEditBooking from './add_edit_booking/add_edit_booking';
import ConfirmationModal from '../admin-common/confirmation/confirmation.modal';

// Add this style object near the top of your component
const sortableHeaderStyle = {
  cursor: 'pointer',
  '&:hover': {
    color: colors.appLightestBlue
  }
};

function BookingList({ date, bookingUpdated }: { date?: string | null | undefined, bookingUpdated?: () => void | undefined }) {
  const dispatch = useDispatch();
  const { bookings, total, page, per_page, filteredBookings } = useSelector(selectBookingsListPageSelector);
  const isAdminOrOwner = useSelector(selectIsAdminOrOwner);
  const loading = useSelector(selectBookingLoading);
  const userList: User[] = useSelector(selectUsersSelector);
  // Filter states
  const [pdfReady, setPdfReady] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const displayBookings = useMemo(() => date ? filteredBookings : bookings, [date, filteredBookings, bookings]);

  // Add state for modals
  const [addEditModalOpened, setAddEditModalOpened] = useState(false);
  const [activeBooking, setActiveBooking] = useState<Booking | null>(null);
  const [confirmationModalOpened, setConfirmationModalOpened] = useState(false);
  const [actionType, setActionType] = useState<string | null>(null);
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [cancelModalOpened, setCancelModalOpened] = useState(false);
  const [shouldUpdateShowCapacity, setShouldUpdateShowCapacity] = useState(true);
  const [shouldUpdateShowCapacityCancel, setShouldUpdateShowCapacityCancel] = useState(true);
  const isMobile = useMediaQuery('(max-width: 50em)');
  const initialParams = {
    page,
    per_page,
    search: '',
    dateRange: 'month',
    date: '',
    startDate: getFormattedDate(),
    endDate: getFormattedDate((getAthensDate(null, false) as DateTime).plus({ month: 1 }).toJSDate()),
    sort_field: 'booking_created_at',
    sort_order: 'desc',
    payment_status: ''
  };
  const [params, setParams] = useState<any>(initialParams);

  const isDayDateFilterApplied = params.date && params.dateRange === 'day';

  const handlePageChange = (newPage: number) => {
    const newParams = { ...params, page: newPage };
    setParams(newParams);
    dispatch(getBookings(newParams));
  };

  const handleClearFilters = () => {
    setParams(initialParams);
    dispatch(getBookings(initialParams as any));
  };

  const handleExportPdf = async () => {
    if (!bookings || bookings.length === 0) return;

    setPdfLoading(true);

    // Create a delay to allow the UI to update with the loading state
    setTimeout(() => {
      setPdfReady(true);
      setPdfLoading(false);
    }, 100);
  };

  const handleSearch = (value: string) => {
    const newParams = { ...params, search: value, page: 1 };
    setParams(newParams);
    dispatch(getBookings(newParams));
  };
  const debouncedSearch = useCallback(debounce(handleSearch, 500), []);

  // Add this function to handle column sorting
  const handleColumnSort = (field: string) => {
    const newParams = { ...params };
    if (params.sort_field === field) {
      // If clicking the same field, toggle the order
      newParams.sort_order = params.sort_order === 'asc' ? 'desc' : 'asc';
    } else {
      // If clicking a new field, set it as the sort field and default to ascending
      newParams.sort_field = field;
      newParams.sort_order = 'asc';
    }
    setParams(newParams);
    dispatch(getBookings(newParams));
  };

  const handleBookingAction = () => {
    if (!activeBooking) return;
    switch (actionType) {
      case 'check-in':
        dispatch(updateBooking({
          id: activeBooking._id,
          data: { is_checked_in: true }
        } as any));
        break;
    }

    setConfirmationModalOpened(false);
    setActiveBooking(null);
    setActionType(null);
    setTimeout(() => {
      bookingUpdated?.();
    }, 400);
  };

  const handleCancelBooking = () => {
    if (!activeBooking) return;

    dispatch(cancelBooking({ 
      id: activeBooking._id,
      updateShowCapacity: shouldUpdateShowCapacityCancel
    } as any));

    setCancelModalOpened(false);
    setActiveBooking(null);
    setShouldUpdateShowCapacityCancel(false);
    setTimeout(() => {
      bookingUpdated?.();
    }, 400);
  };

  const handleDeleteBooking = () => {
    if (!activeBooking) return;

    dispatch(deleteBooking({
      id: activeBooking._id,
      updateShowCapacity: shouldUpdateShowCapacity
    } as any));

    setDeleteModalOpened(false);
    setActiveBooking(null);
    setShouldUpdateShowCapacity(false);
    setTimeout(() => {
      bookingUpdated?.();
    }, 400);
  };

  const handleDateRangeChange = (value: string) => {
    let startDate, endDate, date;
    switch (value) {
      case 'day':
        date = getFormattedDate();
        startDate = null;
        endDate = null;
        setShowCustomDateRange(true);
        break;
      case 'week':
        date = null;
        startDate = getFormattedDate();
        endDate = getFormattedDate((getAthensDate(null, false) as DateTime).plus({ days: 7 }).toJSDate());
        setShowCustomDateRange(false);
        break;
      case 'month':
        date = null;
        startDate = getFormattedDate();
        endDate = getFormattedDate((getAthensDate(null, false) as DateTime).plus({ month: 1 }).toJSDate());
        setShowCustomDateRange(false);
        break;
      case 'year': {
        date = null;
        const currentYear = (getAthensDate(null, false) as DateTime).year;
        startDate = getFormattedDate(DateTime.fromObject({ year: currentYear, month: 1, day: 1 }).toJSDate());
        endDate = getFormattedDate(DateTime.fromObject({ year: currentYear, month: 12, day: 31 }).toJSDate());
        setShowCustomDateRange(false);
        break;
      }
      case 'custom':
        setParams({ ...params, dateRange: value, date: null, page: 1 });
        setShowCustomDateRange(true);
        return;
    }

    const newParams = {
      ...params,
      dateRange: value,
      date,
      startDate,
      endDate,
      page: 1
    };
    setParams(newParams);
    dispatch(getBookings(newParams));
  };

  const handleCustomDateChange = (dates: [Date | null, Date | null]) => {
    const newParams = {
      ...params,
      startDate: dates[0] ? getFormattedDate(dates[0]) : null,
      endDate: dates[1] ? getFormattedDate(dates[1]) : null,
      page: 1
    };
    setParams(newParams);
    if (!dates[1]) return;
    dispatch(getBookings(newParams));
  };

  const handleDayDateChange = (date: Date | null) => {
    const newParams = {
      ...params,
      date: date ? getFormattedDate(date) : null,
      startDate: null,
      endDate: null,
      page: 1
    };
    setParams(newParams);
    dispatch(getBookings(newParams));
  };

  const getCreatedByUser = (createdBy: string) => {
    const user = userList.find(user => user._id === createdBy);
    return user ? `${user.first_name} ${user.last_name} (Employee)` : createdBy || 'Unknown';
  };

  const rows = useMemo(() => displayBookings?.map((booking: Booking, index: number) => (
    <Table.Tr
      key={booking._id}
      style={{
        backgroundColor: index % 2 === 1 ? '#f9f9f9' : 'transparent',
        borderLeft: `5px solid ${getPaymentStatusColor(booking.payment_status)}`
      }}
    >
      <Table.Td>{index + 1}</Table.Td>
      <Table.Td>
        <Stack gap="xs">
          <Text fw={500}>{booking.short_id?.replace(/-/g, '\u2011')}</Text>
          <Text size="sm" style={{ color: booking.booking_type?.toLowerCase() === BookingType.VIP.toLowerCase() ? colors.appDarkPink : colors.appDarkBlue, fontWeight: 700 }}>
            {booking.booking_type?.toLowerCase() === BookingType.VIP.toLowerCase() ? 'VIP TICKET' : 'STANDARD TICKET'}
          </Text>
        </Stack>
      </Table.Td>
      <Table.Td>
        <Stack gap="xs">
          <Text fw={500}>{booking.customer_name?.replace(/-/g, '\u2011').replace(/ /g, '\u00A0')}</Text>
          <Text size="sm">{booking.customer_email}</Text>
          {booking.customer_phone && <Text size="sm">{booking.customer_phone}</Text>}
          {booking.notes && <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap', maxWidth: 250 }}>Notes: {booking.notes}</Text>}
        </Stack>
      </Table.Td>
      <Table.Td>
        <Stack gap="xs">
          <Text style={{ color: getBookingSourceColor(booking.created_source as any), fontWeight: 700 }}>
            {booking.created_source?.replace(/_/g, ' ').toUpperCase()}
          </Text>
          <Text size="sm">Source: {getCreatedByUser(booking.created_by)}</Text>
        </Stack>
      </Table.Td>
      <Table.Td>{new Date(booking.booking_date).toLocaleDateString()}</Table.Td>
      <Table.Td>{booking.booking_created_at ? new Date(booking.booking_created_at).toLocaleDateString() : ''}</Table.Td>
      <Table.Td>{booking.booked_total_seats}</Table.Td>
      <Table.Td>{booking.total_price.toFixed(2)}</Table.Td>
      <Table.Td>
        <Stack gap="xs">
          <Badge color={getPaymentStatusColor(booking.payment_status)}>
            {booking?.payment_status?.replace(/_/g, ' ')?.split(' ')?.map(word =>
              word?.charAt(0)?.toUpperCase() + word?.slice(1)?.toLowerCase()
            )?.join(' ')}
          </Badge>
          <Badge color={booking.is_checked_in ? 'green' : 'gray'}>
            {booking.is_checked_in ? 'Checked In' : 'Not Checked In'}
          </Badge>
        </Stack>
      </Table.Td>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <Tooltip label="Check In">
            <ActionIcon
              size="lg"
              variant="outline"
              color="green"
              disabled={booking.is_checked_in || booking.payment_status === 'cancelled'}
              onClick={() => {
                setActionType('check-in');
                setActiveBooking(booking);
                setConfirmationModalOpened(true);
              }}
            >
              <IconCheck size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Edit Booking">
            <ActionIcon
              size="lg"
              disabled={booking.payment_status === 'cancelled'}
              variant="outline"
              color="blue"
              onClick={() => {
                setAddEditModalOpened(true);
                setActiveBooking(booking);
              }}
            >
              <IconEdit size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
        <Group gap="xs" wrap="nowrap" mt={10}>
          <Tooltip label="Cancel Booking">
            <ActionIcon
              size="lg"
              disabled={booking.payment_status === 'cancelled' || !isAdminOrOwner}
              variant="outline"
              color="grey"
              onClick={() => {
                setActiveBooking(booking);
                setCancelModalOpened(true);
                setShouldUpdateShowCapacityCancel(true);
              }}
            >
              <IconX size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Delete Booking">
            <ActionIcon
              size="lg"
              disabled={!isAdminOrOwner}
              variant="outline"
              color="red"
              onClick={() => {
                setActiveBooking(booking);
                setDeleteModalOpened(true);
                setShouldUpdateShowCapacity(booking.payment_status !== BookingPaymentStatus.PENDING);
              }}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  )), [displayBookings, isAdminOrOwner, ]);

  const pdfFilters = {
    search: params.search,
    date: params.date,
    dateRange: params.dateRange,
    startDate: params.startDate,
    endDate: params.endDate
  };

  useEffect(() => {
    if (date) {
      const newParams = { ...params, date, page: 1, dateRange: 'day', per_page: 100, startDate: null, endDate: null, sort_field: 'booking_created_at', sort_order: 'desc' };
      setParams(newParams);
      dispatch(getBookings(newParams as any));
    } else {
      setParams(initialParams);
      dispatch(getBookings(initialParams as any));
    }
  }, [date]);


  useEffect(() => {
    if (!userList.length) {
      dispatch(getAllUsers());
    }
  }, [])

  // Add this function to format payment status for display
  const formatPaymentStatus = (status: string) => {
    return status?.replace(/_/g, ' ')?.split(' ')?.map(word =>
      word?.charAt(0)?.toUpperCase() + word?.slice(1)?.toLowerCase()
    )?.join(' ');
  };

  // Add this constant for payment status options
  const paymentStatusOptions = Object.values(BookingPaymentStatus).map(status => ({
    value: status,
    label: formatPaymentStatus(status)
  }));

  return (
    <>
      {/* Add/Edit Booking Modal */}
      <AddEditBooking
        opened={addEditModalOpened}
        onClose={() => {
          // If booking list is opened from calendar(show-list), refresh the bookings list with the new date
          if (bookingUpdated && date) {
            const newParams = { ...params, date, page: 1, dateRange: 'day', per_page: 100, startDate: null, endDate: null, sort_field: 'booking_created_at', sort_order: 'desc' };
            setParams(newParams);
            setTimeout(() => {
              dispatch(getBookings(newParams as any));
              bookingUpdated();
            }, 800);
          }
          setAddEditModalOpened(false);
          setActiveBooking(null);
        }}
        activeBooking={activeBooking}
      />
      {/* Check In Booking Modal */}
      <ConfirmationModal
        opened={confirmationModalOpened}
        onClose={() => {
          setConfirmationModalOpened(false);
          setActiveBooking(null);
          setActionType(null);
        }}
        onConfirm={handleBookingAction}
        text={(() => {
          switch (actionType) {
            case 'check-in':
              return `Are you sure you want to check in ${activeBooking?.short_id}?`;
            default:
              return '';
          }
        })()}
      />
      
      {/* Cancel Booking Modal */}
      <Modal
        opened={cancelModalOpened}
        onClose={() => {
          setCancelModalOpened(false);
          setActiveBooking(null);
          setShouldUpdateShowCapacityCancel(false);
        }}
        title="Cancel Booking"
        radius="md"
        size="lg"
        fullScreen={isMobile}
        transitionProps={{ transition: 'fade', duration: 200 }}
        scrollAreaComponent={ScrollArea.Autosize}
        removeScrollProps={{ allowPinchZoom: true }}
      >
        <Stack>
          <Text fw={500}>Are you sure you want to cancel the booking {activeBooking?.short_id}?</Text>
          {activeBooking?.payment_status === BookingPaymentStatus.PENDING && <Text fw={500} c="red">This booking is PENDING. No seats will be returned to the show only if you click the checkbox.</Text>}
          <Checkbox
            label="Update show capacity (increase available seats)"
            checked={shouldUpdateShowCapacityCancel}
            onChange={(event) => setShouldUpdateShowCapacityCancel(event.currentTarget.checked)}
          />
          <Group justify="flex-end" mt="md">
            <Button
              variant="outline"
              onClick={() => {
                setCancelModalOpened(false);
                setActiveBooking(null);
                setShouldUpdateShowCapacityCancel(false);
              }}
            >
              Cancel
            </Button>
            <Button color="orange" onClick={handleCancelBooking}>Cancel Booking</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete Booking Modal */}
      <Modal
        opened={deleteModalOpened}
        onClose={() => {
          setDeleteModalOpened(false);
          setActiveBooking(null);
          setShouldUpdateShowCapacity(false);
        }}
        title="Delete Booking"
        radius="md"
        size="lg"
        fullScreen={isMobile}
        transitionProps={{ transition: 'fade', duration: 200 }}
        scrollAreaComponent={ScrollArea.Autosize}
        removeScrollProps={{ allowPinchZoom: true }}
      >
        <Stack>
          <Text fw={500}>Are you sure you want to delete the booking {activeBooking?.short_id}?</Text>
          {activeBooking?.payment_status === BookingPaymentStatus.PENDING && <Text fw={500} c="red">This booking is PENDING. No seats will be returned to the show only if you click the checkbox.</Text>}
          <Checkbox
            label="Update show capacity (increase available seats)"
            checked={shouldUpdateShowCapacity}
            onChange={(event) => setShouldUpdateShowCapacity(event.currentTarget.checked)}
          />
          <Group justify="flex-end" mt="md">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteModalOpened(false);
                setActiveBooking(null);
                setShouldUpdateShowCapacity(false);
              }}
            >
              Cancel
            </Button>
            <Button color="red" onClick={handleDeleteBooking}>Delete</Button>
          </Group>
        </Stack>
      </Modal>

      {!date && <Grid mb="md">
        <Grid.Col span={12}>
          <Group justify="space-between">
            <Button variant="outline" onClick={handleClearFilters}>
              Clear Filters
            </Button>
            <Group>
              <Button
                variant="filled"
                onClick={() => {
                  setAddEditModalOpened(true);
                  setActiveBooking(null);
                }}
              >
                Add Booking
              </Button>
              {pdfReady ? (
                <PDFDownloadLink
                  document={<BookingsPDF bookings={bookings} filters={pdfFilters} />}
                  fileName={`bookings-report-${getFormattedDate(null, 'yy-MM-dd HH:mm:ss')}.pdf`}
                  style={{ textDecoration: 'none' }}
                >
                  {({ loading: pdfDownloadLoading }) => {
                    return (
                      <Button
                        variant="filled"
                        color="green"
                        leftSection={<IconFileDownload size={16} />}
                        loading={pdfDownloadLoading}
                        onClick={() => {
                          setTimeout(() => {
                            setPdfReady(false);
                            setPdfLoading(false);
                          }, 2000);
                        }}
                      >
                        Download PDF
                      </Button>
                    );
                  }}
                </PDFDownloadLink>
              ) : (
                <Button
                  variant="filled"
                  color="blue"
                  onClick={handleExportPdf}
                  leftSection={<IconFileDownload size={16} />}
                  loading={pdfLoading}
                >
                  Export as PDF
                </Button>
              )}
            </Group>
          </Group>
        </Grid.Col>
        <Grid.Col span={12}>
          <Group px="sm" align="flex-end" justify="flex-start">
            <TextInput
              label="Search"
              placeholder="Search bookings..."
              value={params.search}
              onChange={(e) => {
                setParams({ ...params, search: e.target.value, page: 1 });
                debouncedSearch(e.target.value);
              }}
              rightSection={!params.search ? <IconSearch size={16} /> : <IconX size={16} onClick={() => {
                const newParams = { ...params, search: '', page: 1 };
                setParams(newParams);
                dispatch(getBookings(newParams));
              }} />}
              style={{ width: '400px' }}
            />
            <Select
              label="Payment Status"
              placeholder="All statuses"
              data={[{ value: '', label: 'All Statuses' }, ...paymentStatusOptions]}
              value={params.payment_status}
              onChange={(value) => {
                const newParams = { ...params, payment_status: value, page: 1 };
                setParams(newParams);
                dispatch(getBookings(newParams));
              }}
              style={{ minWidth: '200px' }}
              maxDropdownHeight={400}
            />
            <Select
              label="Items per page"
              placeholder="Items per page"
              data={[
                { value: '10', label: '10' },
                { value: '25', label: '25' },
                { value: '50', label: '50' },
                { value: '100', label: '100' }
              ]}
              value={params.per_page.toString()}
              onChange={(value) => {
                const newParams = { ...params, per_page: Number(value), page: 1 };
                setParams(newParams);
                dispatch(getBookings(newParams));
              }}
            />

            <Stack gap={0}>
              <Text fz={13} fw={500}>Event Date Filter</Text>
              <SegmentedControl
                radius="md"
                color={colors.appDarkBlue}
                transitionDuration={200}
                transitionTimingFunction="linear"
                value={params.dateRange}
                onChange={handleDateRangeChange}
                data={[
                  { label: 'Day', value: 'day' },
                  { label: 'Week', value: 'week' },
                  { label: 'Month', value: 'month' },
                  { label: 'Year', value: 'year' },
                  { label: 'Custom', value: 'custom' }
                ]}
              />
            </Stack>

            {showCustomDateRange && (
              <DatePickerInput
                type={isDayDateFilterApplied ? "default" : "range"}
                label={isDayDateFilterApplied ? "Event Date" : "Custom date range"}
                placeholder={isDayDateFilterApplied ? "Pick event date" : "Pick dates range"}
                value={isDayDateFilterApplied ? (getAthensDate(params.date) as Date || null) : [
                  params.startDate ? getAthensDate(params.startDate) as Date : null,
                  params.endDate ? getAthensDate(params.endDate) as Date : null
                ]}
                onChange={isDayDateFilterApplied ? handleDayDateChange : handleCustomDateChange as any}
              />
            )}
          </Group>
        </Grid.Col>
      </Grid>}

      <Box style={{ position: 'relative' }}>
        <LoadingOverlay
          visible={loading}
          overlayProps={{ radius: "md", blur: 0.5 }}
          loaderProps={{ color: colors.appDarkBlue, type: 'bars' }}
        />
        <Table stickyHeader stickyHeaderOffset={60}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>No.</Table.Th>
              <Table.Th>Booking{'\u00A0'}Details</Table.Th>
              {[
                { field: 'customer_name', label: 'Customer Details' },
                { field: 'created_source', label: 'Source Details' },
                { field: 'booking_date', label: 'Event Date' },
                { field: 'booking_created_at', label: 'Created Date' },
                { field: 'booked_total_seats', label: 'Seats' },
                { field: 'total_price', label: 'Total Price' },
                { field: 'payment_status', label: 'Status' },
              ].map((column, index) => (
                <Table.Th
                  key={index}
                  style={sortableHeaderStyle}
                  onClick={() => handleColumnSort(column.field)}
                >
                  <Group wrap="nowrap" gap="xs" justify="flex-start" align="center" style={{ color: params.sort_field === column.field ? colors.appDarkBlue : colors.appBlack }}>
                    {column?.label?.replace(/ /g, '\u00A0')}{'\u00A0'}{params.sort_field === column.field && (params.sort_order === 'asc' ? '↑' : '↓')}
                  </Group>
                </Table.Th>
              ))}
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
        </Table>
      </Box>

      <Group justify="center" mt="md">
        <Pagination
          total={Math.ceil(total / params.per_page)}
          value={params.page}
          onChange={handlePageChange}
        />
      </Group>
    </>
  );
}

export default BookingList;
